<template>
  <div v-loading="pageLoading" class="dialog-order-list">
    <div v-if="!inPlanning" style="clear:both;">
      <div style="width:20%;float:left;padding-bottom:5px">
        <div style="margin-bottom: 5px">
          <label>{{ $t('series.addOrderNumbers') }}</label>
        </div>
        <el-input
          ref="saveTagInput"
          v-model="orderNumberToAdd"
          class="input-new-tag"
          size="mini"
          style="width:257px;padding-bottom:3px;"
          placeholder="LB1234567890/01(/01)"
          @keyup.enter.native="handleInputConfirm"
          @keyup.space.native="handleInputConfirm"
          @blur="handleInputConfirm"
        >
        </el-input>
        <div v-if="showOrderNumberError">
          <label style="color:red"> {{ $t('series.doubleOrderNumber') }} </label>
        </div>
        <el-tag
          v-for="orderNumber in orderNumbers"
          :key="orderNumber"
          style="margin-right:5px;margin-top:2px"
          closable
          :disable-transitions="false"
          @close="handleClose(orderNumber)"
        >
          {{ orderNumber }}
        </el-tag>
        <span style="margin-top:3px">
          <editor-button
            v-if="orderNumbers.length > 0"
            :icon-only="false"
            :disabled="pageLoading"
            button-translation="series.addOrderNumbers"
            action="primary"
            style="margin-top:3px"
            @click="addOrderNumbersToSeries()"
          />
        </span>
      </div>
      <div style="width:80%;float:right;padding-bottom:5px">
        <div style="margin-bottom: 5px">
          <label>{{ $t('series.bulkActionLabel') }}</label>
        </div>
        <div>
          <el-date-picker
            v-model="estimatedDeliveryDate"
            :format="dateFormat"
            value-format="yyyy-MM-dd"
            :placeholder="$t('orderLine.estimatedDeliveryDate')"
          />
          <el-select v-model="fromStatusID" type="select" :placeholder="$t('orderLine.fromStatus')" clearable>
            <el-option-group :label="$t('orderLine.status')">
              <el-option
                v-for="option in this.$store.getters['orderLineStatus/loadedItems'].filter(
                  s => s.settableYN || statusesInSeries.includes(s.id)
                )"
                :key="option.id"
                :label="option.name"
                :value="option.id"
              />
            </el-option-group>
          </el-select>
          <el-select v-model="toStatusID" type="select" :placeholder="$t('orderLine.toStatus')" clearable>
            <el-option-group :label="$t('orderLine.status')">
              <el-option
                v-for="option in this.$store.getters['orderLineStatus/loadedItems'].filter(s => s.settableYN)"
                :key="option.id"
                :label="option.name"
                :value="option.id"
              />
            </el-option-group>
          </el-select>
          <editor-button
            :icon-only="false"
            :disabled="pageLoading || !hasItemsSelected || (!toStatusID && !estimatedDeliveryDate)"
            button-translation="series.updateOrderLines"
            action="primary"
            @click="updateSeriesOrderLineStatusesWithStatusAfterConfirmation()"
          />
          <editor-button
            :icon-only="false"
            :disabled="pageLoading || !hasItemsSelected"
            button-translation="series.printTickets"
            action="primary"
            @click="printSelectedOrderLinesAfterConfirmation()"
          />
          <editor-button
            :icon-only="false"
            :disabled="pageLoading || !hasItemsSelected"
            button-translation="common.deletePlural"
            action="delete"
            @click="deleteSelectedOrderLinesFromSeriesAfterConfirmation()"
          />
        </div>
      </div>
    </div>
    <select-printer-client-dialog
      :dialog-visible.sync="printDialogVisible"
      :on-select="printerClientSelected"
      :on-cancel="printerClientSelectionCancelled"
    />
    <el-table
      ref="dialogOrderList"
      :data="items"
      border
      fit
      highlight-current-row
      style="width: 100%;"
      height="600"
      @row-dblclick="rowDblClick"
      @selection-change="handleSelectionChange"
    >
      <el-table-column :label="$t('orderLine.ticketNumber')" property="orderNumber" width="150">
        <template slot-scope="scope">
          <span>{{ scope.row['ticketNumber'] }}</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('orderLine.consumerName')" property="consumerName">
        <template slot-scope="scope">
          <span>{{ scope.row['consumerName'] }}</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('orderLine.fabricName')" property="fabricDescription" min-width="100">
        <template slot-scope="scope">
          <span>{{ scope.row['fabricDescription'] }}</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('orderLine.orderDate')" property="orderedAt" width="150">
        <template slot-scope="scope">
          <span><date-time-picker v-model="scope.row['orderedAt']" :format="dateTimeFormat" disabled/></span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('orderLine.estimatedDeliveryDate')" property="estimatedDeliveryDate" width="150">
        <template slot-scope="scope">
          <span><date-time-picker v-model="scope.row['estimatedDeliveryDate']" :format="dateFormat" disabled/></span>
        </template>
      </el-table-column>
      <el-table-column :label="amountString" property="totalCapacity" width="100px">
        <template slot-scope="scope">
          <span>{{ scope.row['totalCapacity'] }}</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('fabric.yarn')" property="yarnDescription" width="60px">
        <template slot-scope="scope">
          <span>{{ scope.row['yarnDescription'] }}</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('orderLine.urgent')" property="urgentYN" width="100px">
        <template slot-scope="scope">
          <span>{{ scope.row['urgentYN'] ? $t('common.yes') : $t('common.no') }}</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('orderLine.status')" property="orderLineSubstatusDescription" width="150">
        <template slot-scope="scope">
          <span>{{ scope.row['orderLineSubstatusDescription'] }}</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('orderLine.printStatusOption')" property="printStatusOption" width="180px">
        <template slot-scope="scope">
          <span>
            <el-alert
              v-if="showPrintStatus(scope.row)"
              :title="getPrintStatusOptionText(scope.row)"
              :type="getPrintStatusOptionAlertType(scope.row)"
              :closable="false"
              show-icon
            >
            </el-alert>
          </span>
        </template>
      </el-table-column>
      <el-table-column
        v-if="!inPlanning"
        type="selection"
        width="40"
        align="center"
        :selectable="() => true"
      ></el-table-column>
    </el-table>
  </div>
</template>

<script>
import { closeCurrentTag } from '@/utils/tags';
import DateTimePicker from '@/components/DSE/DateTimePicker';
import EditorButton from '@/components/crud/EditorButton';
import { PlanningOption, PrintStatusOption } from '@/utils/constants';
import SelectPrinterClientDialog from '@/pages/components/SelectPrinterClientDialog';
export default {
  name: 'DialogOrderList',
  components: {
    EditorButton,
    DateTimePicker,
    SelectPrinterClientDialog
  },
  props: {
    entity: {
      default: '',
      type: String
    },
    permissionCode: String,
    seriesID: {
      default: 0,
      type: Number
    },
    inPlanning: {
      default: false,
      type: Boolean
    },
    planningOption: {
      type: Number
    }
  },
  data() {
    return {
      pageLoading: false,
      dialogVisible: true,
      amountString: '',
      items: [],
      multipleSelection: [],

      orderNumbers: [],
      orderNumberToAdd: '',
      showOrderNumberError: false,
      // order line status update
      fromStatusID: null,
      toStatusID: null,
      estimatedDeliveryDate: null,
      printDialogVisible: false,
      statusesInSeries: []
    };
  },
  computed: {
    dateFormat() {
      return this.$store.getters.dateFormat;
    },
    dateTimeFormat() {
      return this.$store.getters.dateTimeFormat;
    },
    hasItemsSelected() {
      return !!this.multipleSelection.length;
    }
  },
  watch: {
    seriesID() {
      this.orderNumbers = [];
      this.showOrderNumberError = false;
      this.getOrdersInSeries();
      this.getRequiredAmountTranslation();
    }
  },
  async created() {
    await this.$store.dispatch('orderLineStatus/getItems');
    this.getOrdersInSeries();
    this.getRequiredAmountTranslation();
  },
  methods: {
    close() {
      closeCurrentTag(this);
    },
    getOrdersInSeries() {
      this.pageLoading = true;
      this.$store
        .dispatch('productionPlanningSeries/getSeries', {
          seriesID: this.seriesID
        })
        .then(data => {
          this.items = data.orderLines;
          this.statusesInSeries = data.orderLines.map(i => i.orderLineStatusID);
        })
        .finally(() => {
          this.pageLoading = false;
        });
    },
    updateSeriesOrderLineStatusesWithStatusAfterConfirmation() {
      this.$store.dispatch('notify/confirm', { message: this.$i18n.t('series.confirmUpdate') }).then(() => {
        this.updateSeriesOrderLineStatusesWithStatus();
      });
    },
    updateSeriesOrderLineStatusesWithStatus() {
      this.pageLoading = true;
      const orderIDs = this.multipleSelection.map(i => i.orderLineID);
      this.$store
        .dispatch('productionPlanningSeries/updateSeriesOrderLineStatusesWithStatus', {
          seriesID: this.seriesID,
          selection: orderIDs,
          fromStatusID: this.fromStatusID || null,
          toStatusID: this.toStatusID,
          estimatedDeliveryDate: this.estimatedDeliveryDate
        })
        .then(data => {
          this.$store.dispatch('notify/success', { message: this.$i18n.t('common.data_updated') });
          this.getOrdersInSeries();
        })
        .finally(() => {
          this.pageLoading = false;
        });
    },
    deleteSelectedOrderLinesFromSeriesAfterConfirmation() {
      this.$store.dispatch('notify/confirm', { message: this.$i18n.t('series.confirmDelete') }).then(() => {
        this.deleteSelectedOrderLinesFromSeries();
      });
    },
    deleteSelectedOrderLinesFromSeries() {
      this.pageLoading = true;
      const orderIDs = this.multipleSelection.map(i => i.orderLineID);
      this.$store
        .dispatch('productionPlanningSeries/deleteSelectedOrderLinesFromSeries', {
          seriesID: this.seriesID,
          selection: orderIDs
        })
        .then(data => {
          this.$store.dispatch('notify/success', { message: this.$i18n.t('common.data_updated') });
          this.getOrdersInSeries();
        })
        .finally(() => {
          this.pageLoading = false;
        });
    },
    async printSelectedOrderLinesAfterConfirmation() {
      this.$store.dispatch('notify/confirm', { message: this.$i18n.t('series.confirmPrint') }).then(() => {
        this.checkForAlreadyPrintedAndPrintAfterConfirmation();
      });
    },
    clearInput() {
      this.$store
        .dispatch('notify/confirm', { message: this.$i18n.t('stockCorrection.clearInputWarning') })
        .then(() => {
          this.refreshList();
          this.changesMadeOnCurrentPage = false;
        })
        .catch(() => {});
    },

    async printerClientSelected(printerClientID) {
      this.printDialogVisible = false;
      await this.doPrintSelectedOrderLines(printerClientID);
    },
    printerClientSelectionCancelled() {
      this.printDialogVisible = false;
    },
    async checkForAlreadyPrintedAndPrintAfterConfirmation() {
      const alreadyPrinted = this.multipleSelection.filter(
        i => i.printStatusOption === PrintStatusOption.Printed || i.printStatusOption === PrintStatusOption.Queued
      );
      if (alreadyPrinted.length > 0) {
        this.$store
          .dispatch('notify/confirm', {
            message: this.$i18n.t('series.ticketsAlreadyPrintedMessage', {
              printedTickets: alreadyPrinted.length,
              totalTickets: this.multipleSelection.length
            })
          })
          .then(() => {
            this.printDialogVisible = true;
          })
          .catch(() => {
            console.log('print cancelled by user');
          });
      } else {
        this.printDialogVisible = true;
      }
    },
    async doPrintSelectedOrderLines(printerClientID) {
      const orderIDs = this.multipleSelection.map(i => i.orderLineID);
      const printCommand = {
        seriesID: this.seriesID,
        printerClientID: printerClientID,
        overrideAlreadyPrinted: true,
        orderLines: orderIDs
      };
      this.$store.dispatch('notify/success', {
        message: this.$i18n.t('series.seriesPrintStart', { toBePrintedCount: orderIDs.length })
      });
      this.$store
        .dispatch('productionPlanningSeries/printTickets', printCommand)
        .then(result => {
          const printDestination =
            this.$store.getters['printerClient/getCurrentPrinterClientName'] ||
            this.$i18n.t('common.plannedWorkshopPrintClient');
          this.actionBusy = false;
          this.$store.dispatch('notify/success', {
            message: this.$i18n.t('series.seriesPrintSuccess', { printDestination })
          });
        })
        .catch(error => {
          console.error(error);
          this.actionBusy = false;
          this.$store.dispatch('notify/error', { message: this.$i18n.t('series.seriesPrintError') });
        });
    },
    rowDblClick(row) {
      this.$router.push({ name: 'EditOrderLine', params: { id: row.orderLineID } });
    },
    getRequiredAmountTranslation() {
      switch (this.planningOption) {
        case PlanningOption.Panel:
          this.amountString = this.$i18n.t('series.numberOfPanels');
          break;
        case PlanningOption.Cutting:
        case PlanningOption.NetCurtainRoomHigh:
        case PlanningOption.CurtainRoomHigh:
          this.amountString = this.$i18n.t('series.numberOfMeters');
          break;
        case PlanningOption.RomanBlind:
          this.amountString = this.$i18n.t('series.pieces');
          break;
      }
    },
    getPrintStatusOptionAlertType(row) {
      let alertType = 'info';
      switch (row.printStatusOption) {
        case PrintStatusOption.Printed:
          alertType = 'success';
          break;
        case PrintStatusOption.Failed:
          alertType = 'error';
          break;
        case PrintStatusOption.Queued:
          alertType = 'info';
          break;
      }
      return alertType;
    },
    getPrintStatusOptionText(row) {
      let printStatusOptionText = '';
      switch (row.printStatusOption) {
        case PrintStatusOption.Printed:
          printStatusOptionText = this.$i18n.t('orderLine.printStatusOptionPrinted');
          break;
        case PrintStatusOption.Failed:
          printStatusOptionText = this.$i18n.t('orderLine.printStatusOptionFailed');
          break;
        case PrintStatusOption.Queued:
          printStatusOptionText = this.$i18n.t('orderLine.printStatusOptionQueued');
          break;
      }
      return printStatusOptionText;
    },
    showPrintStatus(row) {
      return row.printStatusOption !== PrintStatusOption.Unattempted;
    },
    handleSelectionChange(val) {
      this.multipleSelection = val;
    },
    selectAllItems(rows) {
      rows.forEach(row => this.$refs.dialogOrderList.toggleRowSelection(row, true));
    },
    clearSelection() {
      this.$refs.dialogOrderList.clearSelection();
    },
    handleClose(orderNumber) {
      this.orderNumbers.splice(this.orderNumbers.indexOf(orderNumber), 1);
    },
    handleInputConfirm() {
      const orderNumberToAdd = this.orderNumberToAdd;
      if (orderNumberToAdd) {
        if (this.orderNumbers.indexOf(orderNumberToAdd) === -1) {
          this.showOrderNumberError = false;
          this.orderNumbers.push(orderNumberToAdd);
        } else {
          this.showOrderNumberError = true;
        }
      }
      this.orderNumberToAdd = '';
    },
    addOrderNumbersToSeries() {
      this.pageLoading = true;
      this.$store
        .dispatch('productionPlanningSeries/addOrderNumbersToSeries', {
          seriesID: this.seriesID,
          orderNumbers: this.orderNumbers
        })
        .then(result => {
          this.$store.dispatch('notify/success', { message: this.$i18n.t('series.orderNumbersAdded') });
          this.orderNumbers = [];
          this.getOrdersInSeries();
        })
        .catch(error => {
          console.error(error);
          this.$store.dispatch('notify/error', { message: this.$i18n.t('series.orderNumbersNotAdded') });
        })
        .finally(() => {
          this.pageLoading = false;
        });
    }
  }
};
</script>
<style lang="scss">
.reset-button {
  background-color: #ff8c8c;
  color: #fff;
  margin-left: 0;
  margin-top: 5px;
  float: right;
}
</style>
